import { useState, useEffect, useRef } from "react";
import { supabase } from "../lib/supabase";
import type { CohortRow } from "../components/dashboard/CohortRevenueStack";

// =============================================================================
// USE COHORT REVENUE STACK — Fetch cohort cash collection data
// =============================================================================
// PURPOSE: Calls get_cohort_revenue_stack RPC and transforms the flat rows
// into the CohortRow[] shape needed by CohortRevenueStack component.
// =============================================================================

const MAX_MONTHS_OUT = 5;

interface UseCohortRevenueStackResult {
  data: CohortRow[];
  isLoading: boolean;
}

export function useCohortRevenueStack(
  organizationId: string | null | undefined
): UseCohortRevenueStackResult {
  const [data, setData] = useState<CohortRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!organizationId || !supabase) {
      setData([]);
      return;
    }

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    async function fetch() {
      setIsLoading(true);
      try {
        const { data: rows, error } = await supabase!.rpc("get_cohort_revenue_stack", {
          p_organization_id: organizationId,
          p_max_months_out: MAX_MONTHS_OUT,
        });

        if (controller.signal.aborted) return;
        if (error) throw error;

        // Transform flat rows into CohortRow[] grouped by cohort_month
        const grouped = new Map<string, Record<number, number>>();
        for (const row of rows || []) {
          const key = row.cohort_month as string;
          if (!grouped.has(key)) grouped.set(key, {});
          grouped.get(key)![row.months_since as number] = parseFloat(String(row.total_cash));
        }

        const result: CohortRow[] = Array.from(grouped.entries())
          .map(([cohort_month, months]) => ({ cohort_month, months }))
          .sort((a, b) => a.cohort_month.localeCompare(b.cohort_month));

        setData(result);
      } catch {
        if (!controller.signal.aborted) setData([]);
      } finally {
        if (!controller.signal.aborted) setIsLoading(false);
      }
    }

    fetch();
    return () => controller.abort();
  }, [organizationId]);

  return { data, isLoading };
}
